<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { ContentBlock } from '~/components/ui/content-block'
import { WidgetNewsSlider } from '~/components/widget/news-slider'
import { WidgetGuaranteeFundContacts } from '~/components/widget/guarantee-fund-contacts'
import { WidgetStaff } from '~/components/widget/staff'
import { SvgIcon } from '~/components/ui/svg-icon'
import { WidgetSeoContent } from '~/components/widget/seo'
import { WidgetReviveAd } from '~/components/widget/revive-ad'

const { t, locale } = useI18n()

const breadcrumbs = computed(() => [{ title: t('common.guarantee_fund'), url: '/guarantee-fund' }])

const fundData = computed(() => {
  const isKyrgyz = locale.value === 'ky'

  return {
    title: isKyrgyz ? 'Кепилдик фонд' : 'Гарантийный фонд',
    officialTitle: isKyrgyz ? '«Кепилдик фонд» ААК' : 'ОАО «Гарантийный фонд»',
    logo: isKyrgyz
      ? '/images/guarantee-fund/guarantee-fund_ky.png'
      : '/images/guarantee-fund/guarantee-fund_ru.png',
    logo_secondary: isKyrgyz
      ? '/images/guarantee-fund/guarantee-fund_square_ky.png'
      : '/images/guarantee-fund/guarantee-fund_square_ru.png',
    website: 'https://gf.kg',
    phones: ['+996 (312) 66-49-60', '+996 (312) 62-53-79'],
    address: isKyrgyz
      ? 'Кыргызстан, Бишкек ш., Чүй пр., 114, 4-кабат, 408-каб.'
      : 'Кыргызстан, г. Бишкек, пр. Чуй, 114, 4 этаж, каб. 408',
    email: 'office@gf.kg',
    socialLinks: [
      {
        url: 'https://www.facebook.com/GarantiynuiFond1/',
        title: 'Facebook',
        className: 'hover:text-facebook',
        path: 'M15.1742 5.40796H17V2.22827C16.6856 2.1853 15.6023 2.08765 14.3409 2.08765C11.7083 2.08765 9.9053 3.7439 9.9053 6.78687V9.58765H7V13.1423H9.9053V22.0876H13.4659V13.1423H16.2538L16.697 9.58765H13.4659V7.13843C13.4659 6.11108 13.7424 5.40796 15.1742 5.40796Z'
      },
      {
        url: 'https://www.instagram.com/guaranteefund_kg/',
        title: 'Instagram',
        className: 'hover:text-instagram',
        path: 'M13.028 2.08765C14.153 2.09065 14.724 2.09665 15.217 2.11065L15.411 2.11765C15.635 2.12565 15.856 2.13565 16.123 2.14765C17.187 2.19765 17.913 2.36565 18.55 2.61265C19.21 2.86665 19.766 3.21065 20.322 3.76565C20.8307 4.26538 21.2242 4.87012 21.475 5.53765C21.722 6.17465 21.89 6.90065 21.94 7.96565C21.952 8.23165 21.962 8.45265 21.97 8.67765L21.976 8.87165C21.991 9.36365 21.997 9.93465 21.999 11.0596L22 11.8056V13.1156C22.0025 13.8451 21.9948 14.5745 21.977 15.3036L21.971 15.4976C21.963 15.7226 21.953 15.9436 21.941 16.2096C21.891 17.2746 21.721 17.9996 21.475 18.6376C21.2242 19.3052 20.8307 19.9099 20.322 20.4096C19.8223 20.9183 19.2175 21.3118 18.55 21.5626C17.913 21.8096 17.187 21.9776 16.123 22.0276L15.411 22.0576L15.217 22.0636C14.724 22.0776 14.153 22.0846 13.028 22.0866L12.282 22.0876H10.973C10.2433 22.0902 9.51354 22.0826 8.78402 22.0646L8.59002 22.0586C8.35263 22.0497 8.11529 22.0393 7.87802 22.0276C6.81402 21.9776 6.08802 21.8096 5.45002 21.5626C4.78285 21.3117 4.17846 20.9182 3.67902 20.4096C3.16996 19.91 2.77613 19.3053 2.52502 18.6376C2.27802 18.0006 2.11002 17.2746 2.06002 16.2096L2.03002 15.4976L2.02502 15.3036C2.00659 14.5745 1.99825 13.8451 2.00002 13.1156V11.0596C1.99725 10.3302 2.00459 9.60084 2.02202 8.87165L2.02902 8.67765C2.03702 8.45265 2.04702 8.23165 2.05902 7.96565C2.10902 6.90065 2.27702 6.17565 2.52402 5.53765C2.77571 4.86984 3.17024 4.26508 3.68002 3.76565C4.17917 3.2572 4.78321 2.86372 5.45002 2.61265C6.08802 2.36565 6.81302 2.19765 7.87802 2.14765C8.14402 2.13565 8.36602 2.12565 8.59002 2.11765L8.78402 2.11165C9.51321 2.09388 10.2426 2.08621 10.972 2.08865L13.028 2.08765ZM12 7.08765C10.6739 7.08765 9.40217 7.61443 8.46449 8.55211C7.5268 9.48979 7.00002 10.7616 7.00002 12.0876C7.00002 13.4137 7.5268 14.6855 8.46449 15.6232C9.40217 16.5609 10.6739 17.0876 12 17.0876C13.3261 17.0876 14.5979 16.5609 15.5356 15.6232C16.4732 14.6855 17 13.4137 17 12.0876C17 10.7616 16.4732 9.48979 15.5356 8.55211C14.5979 7.61443 13.3261 7.08765 12 7.08765ZM12 9.08765C12.394 9.08758 12.7841 9.16511 13.1481 9.31582C13.5121 9.46652 13.8429 9.68744 14.1215 9.96597C14.4001 10.2445 14.6211 10.5752 14.772 10.9391C14.9228 11.3031 15.0005 11.6932 15.0005 12.0871C15.0006 12.4811 14.9231 12.8712 14.7723 13.2352C14.6216 13.5992 14.4007 13.93 14.1222 14.2086C13.8437 14.4872 13.513 14.7083 13.149 14.8591C12.7851 15.0099 12.395 15.0876 12.001 15.0876C11.2054 15.0876 10.4423 14.7716 9.8797 14.209C9.31709 13.6464 9.00102 12.8833 9.00102 12.0876C9.00102 11.292 9.31709 10.5289 9.8797 9.96633C10.4423 9.40372 11.2054 9.08765 12.001 9.08765M17.251 5.58765C16.9195 5.58765 16.6016 5.71934 16.3671 5.95376C16.1327 6.18818 16.001 6.50613 16.001 6.83765C16.001 7.16917 16.1327 7.48711 16.3671 7.72153C16.6016 7.95595 16.9195 8.08765 17.251 8.08765C17.5825 8.08765 17.9005 7.95595 18.1349 7.72153C18.3693 7.48711 18.501 7.16917 18.501 6.83765C18.501 6.50613 18.3693 6.18818 18.1349 5.95376C17.9005 5.71934 17.5825 5.58765 17.251 5.58765Z'
      },
      {
        url: 'https://www.linkedin.com/company/77686467/',
        title: 'LinkedIn',
        className: 'hover:text-linkedin',
        path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z'
      }
    ],
    staff: [
      {
        id: 1,
        photo: '/images/guarantee-fund/image1.png',
        fullname: 'Маматалиев Абдырахман Мадакимович',
        position: isKyrgyz ? 'Директорлор кеңешинин Төрагасы' : 'Председатель Совета директоров'
      },
      {
        id: 2,
        photo: '/images/guarantee-fund/image2.png',
        fullname: 'Турганбаев Надырбек Турганбаевич',
        position: isKyrgyz ? 'Директорлор кеңешинин мүчөсү' : 'Член Совета директоров'
      },
      {
        id: 3,
        photo: '/images/guarantee-fund/image3.png',
        fullname: 'Чокоев Заир Линарович',
        position: isKyrgyz ? 'Директорлор кеңешинин мүчөсү' : 'Член Совета директоров'
      },
      {
        id: 4,
        photo: '/images/guarantee-fund/image4.png',
        fullname: 'Абдыров Толонбек Шакирович',
        position: isKyrgyz ? 'Директорлор кеңешинин мүчөсү' : 'Член Совета директоров'
      },
      {
        id: 5,
        photo: '/images/guarantee-fund/image5.png',
        fullname: 'Тоялиев Улан Мамбеткович',
        position: isKyrgyz ? 'Директорлор кеңешинин мүчөсү' : 'Член Совета директоров'
      },
      {
        id: 6,
        photo: '/images/guarantee-fund/image6.png',
        fullname: 'Абакиров Малик-Айдар Барктабасович',
        position: isKyrgyz ? 'Башкарманын Төрагасы' : 'Председатель Правления'
      },
      {
        id: 7,
        photo: '/images/guarantee-fund/image7.png',
        fullname: 'Таласбаев Эрик Нурдинович',
        position: isKyrgyz
          ? 'Башкарма Төрагасынын орун басары'
          : 'Заместитель Председателя Правления'
      },
      {
        id: 8,
        photo: '/images/guarantee-fund/image8.png',
        fullname: 'Курманкожоева Рейна Садырбековна',
        position: isKyrgyz
          ? 'Башкарма Төрагасынын орун басары'
          : 'Заместитель Председателя Правления'
      },
      {
        id: 9,
        photo: '/images/guarantee-fund/image9.png',
        fullname: 'Айбалаева Айнура Маматовна',
        position: isKyrgyz ? 'Башкарманын мүчөсү' : 'Член Правления'
      },
      {
        id: 10,
        photo: '/images/guarantee-fund/image10.png',
        fullname: 'Узакова Батма Камбаровна',
        position: isKyrgyz ? 'Башкарманын мүчөсү' : 'Член Правления'
      }
    ]
  }
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetGuaranteeFundContacts :contacts="fundData" />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
          </aside>
        </template>
        <div class="space-y-5">
          <ContentBlock variant="white">
            <article class="space-y-6">
              <div class="flex flex-col gap-3 sm:gap-6 md:flex-row-reverse md:justify-between">
                <div class="rounded-md md:max-w-80">
                  <NuxtImg
                    :src="fundData.logo"
                    :alt="fundData.title"
                    class="w-34"
                    fetchpriority="high"
                  />
                </div>
                <div class="space-y-1">
                  <Heading tag="h1" class="font-serif text-xl !leading-8 sm:!text-2xl md:!text-3xl">
                    {{ fundData.title }}
                  </Heading>
                  <div class="mt-1 text-sm leading-3 text-gray-600">
                    <p>{{ fundData.officialTitle }}</p>
                  </div>

                  <div v-if="fundData.socialLinks.length" class="flex items-center gap-2 pt-2">
                    <a
                      v-for="social in fundData.socialLinks"
                      :key="social.url"
                      :href="social.url"
                      :title="social.title"
                      :class="social.className"
                      class="block text-gray-500 transition"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <SvgIcon :path="social.path" class="size-5" />
                    </a>
                  </div>
                </div>
              </div>
            </article>
          </ContentBlock>

          <WidgetSeoContent position="upper" heading-level="h2" />

          <section v-if="fundData.staff && fundData.staff.length">
            <ContentBlock
              :heading-text="t('page.guarantee_fund.leadership')"
              :heading-tag="'h2'"
              heading-class="h4"
            >
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <WidgetStaff
                  v-for="item in fundData.staff"
                  :key="item.id"
                  variant="white"
                  v-bind="item"
                />
              </div>
            </ContentBlock>
          </section>

          <WidgetSeoContent position="bottom" />

          <WidgetNewsSlider
            :heading="$t('common.news')"
            hide-image
            hide-add-to-favorites
            :slides-per-view-md="3"
            :slides-per-view-sm="3"
            :query-params="{
              per_page: '10',
              'filter[tag]': 'ГФ,Гарантийный фонд',
              'filter[type]': 'news'
            }"
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
